import {
    initVelt,
    identifyUserWithVelt,
    configureVeltAfterIdentified,
    sendDocumentIdToVelt,
    setVeltPermissionProvider,
    signOutWithVelt,
} from '@/collaboration/collaboration.utils';
import { hasSubscriptionFlag, hasSegmentFlag } from '@/data/flags/local-flags.ts';
import { calculateUsernameColor, formatUserFullName } from '@utils/user-settings';

export function getInitialState () {
    return {
        initialized: false,
        drawerOpen: false,
        documentId: null,
        documentContext: null,
        visibleThreadId: '',
    };
}

function getActiveSubscriptionId (rootGetters) {
    if (!rootGetters?.['auth/isAuthenticated']) {
        return null;
    }

    return rootGetters?.['subscription/getActiveSub']?.id || null;
}

const collaboration = {
    namespaced: true,
    state: getInitialState(),
    mutations: {
        setInitialized (state, { initialized }) {
            if (window.Cypress) {
                window.veltInitialized = initialized;
            }

            state.initialized = initialized;
        },
        setDrawerOpen (state, { drawerOpen }) {
            state.drawerOpen = drawerOpen;
        },
        setDocumentId (state, { documentId, context }) {
            state.documentId = documentId;
            state.documentContext = context || null;
        },
        setVisibleThreadId (state, { visibleThreadId }) {
            state.visibleThreadId = visibleThreadId;
        },
    },
    actions: {
        async initCollaboration ({ commit, dispatch, state, rootState }) {
            if (!hasSubscriptionFlag('veltIntegration') || !hasSegmentFlag('veltMigration')) return;

            const { user } = rootState.auth;
            const { id: userId, username: email, subscriptionId, velt: { apiKey, token } = {} } = user;
            const canInitializeVelt = apiKey && token;

            if (!canInitializeVelt) return;

            try {
                await initVelt(apiKey);
            } catch {
                console.error('Unable to initialize velt, disabling comments');

                return;
            }

            setVeltPermissionProvider();

            const colorObj = calculateUsernameColor(email);

            const veltUser = {
                userId,
                name: formatUserFullName(user),
                email,
                organizationId: subscriptionId,
                color: colorObj['background-color'],
                textColor: colorObj.color,
            };

            let initialIdentifyFailed = false;

            try {
                await identifyUserWithVelt(veltUser, token);
            } catch (err) {
                initialIdentifyFailed = true;
                console.error('Initial identification with velt failed, retrying...', err);
            }

            if(initialIdentifyFailed) {
                try {
                    await dispatch('auth/refreshVeltToken', null, { root: true });

                    const newToken = rootState.auth.user?.velt?.token;

                    await identifyUserWithVelt(veltUser, newToken);
                } catch(err) {
                    console.error('Failed to authenticate with velt, disabling comments.', err);

                    return;
                }
            }

            await configureVeltAfterIdentified(state.documentId, state.documentContext);

            commit('setInitialized', { initialized: true });
        },
        toggleDrawer ({ commit, state }) {
            commit('setDrawerOpen', {
                drawerOpen: !state.drawerOpen,
            });
        },
        toggleThreadVisibility ({ commit, state }, threadId) {
            if (state.visibleThreadId === threadId) {
                commit('setVisibleThreadId', { visibleThreadId: '' });

                return;
            }
            commit('setVisibleThreadId', { visibleThreadId: threadId });
        },
        // We temporarily need to track the document id like this while collaboration
        // is behind a segment flag, as we need to conditionally initialize velt in a way that doesn't
        // cause us to import the router into our utils file. Once velt is soley behind a subscription flag,
        // we can reduce this back to a more direct veltClient.setDocumentId call and remove our vuex action/state
        async updateDocumentId ({ commit }, payload) {
            const { documentId, context } = payload || {};
            commit('setDocumentId', { documentId, context });
            await sendDocumentIdToVelt({ documentId, context });
        },
        signOut ({ commit, state }) {
            if (state.initialized) {
                signOutWithVelt();
                commit('setInitialized', { initialized: false });
            }
        },
    },
    getters: {
        hasCollaborationAccess: (state) => {
            const { initialized } = state;

            return initialized;
        },
        hasListenCommentsAccess: (state, _getters, _rootState, rootGetters) => {
            const { initialized } = state;
            const activeSubscriptionId = getActiveSubscriptionId(rootGetters);

            if (!activeSubscriptionId) {
                return false;
            }

            return (
                hasSubscriptionFlag('veltIntegration', activeSubscriptionId) &&
                hasSegmentFlag('veltMigration') &&
                initialized
            );
        },
        hasListenCommentsAccessWithoutInitialization: (state, _getters, _rootState, rootGetters) => {
            const { initialized } = state;
            const activeSubscriptionId = getActiveSubscriptionId(rootGetters);

            if (!activeSubscriptionId) {
                return false;
            }

            return (
                hasSubscriptionFlag('veltIntegration', activeSubscriptionId) &&
                hasSegmentFlag('veltMigration') &&
                !initialized
            );
        },
    },
};

export default collaboration;
